import { USER_INFO_KEY } from './persistenceKeys';
import * as codec from './persistenceCodec';
import {
  PersistenceRemoveError,
  PersistenceSerializationError,
  PersistenceWriteError,
} from './persistenceErrors';
import { resolvePersistenceReporter, safeReport } from './persistenceReporter';
import { isSyncStorageDriver } from './syncStorageDriver';

/**
 * @param {unknown} value
 * @returns {value is Record<string, unknown>}
 */
function isAuthBlobObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * @param {import('./syncStorageDriver').SyncStorageDriver} driver
 * @param {import('./persistenceReporter').PersistenceReporter} reporter
 */
function attemptClearUserInfo(driver, reporter) {
  try {
    driver.removeItem(USER_INFO_KEY);
  } catch (cause) {
    safeReport(reporter, 'persistence_clear_failure', {
      key: USER_INFO_KEY,
      operation: 'removeItem',
      errorName: cause?.name || 'Error',
    });
  }
}

/**
 * @param {{
 *   driver: import('./syncStorageDriver').SyncStorageDriver,
 *   reporter?: import('./persistenceReporter').PersistenceReporter,
 * }} options
 */
export function createAuthPersistence({ driver, reporter }) {
  if (!isSyncStorageDriver(driver)) {
    throw new Error('createAuthPersistence requires a synchronous storage driver');
  }

  const report = resolvePersistenceReporter(reporter);

  function readUserInfoBlob() {
    let raw;
    try {
      raw = driver.getItem(USER_INFO_KEY);
    } catch (cause) {
      safeReport(report, 'persistence_read_failure', {
        key: USER_INFO_KEY,
        operation: 'getItem',
        errorName: cause?.name || 'Error',
      });
      return null;
    }

    const decoded = codec.decode(raw);
    if (!decoded.ok) {
      if (decoded.kind === 'missing') {
        return null;
      }

      safeReport(report, 'persistence_parse_failure', {
        key: USER_INFO_KEY,
        operation: 'decode',
        errorName: decoded.error?.name || 'SyntaxError',
      });
      attemptClearUserInfo(driver, report);
      return null;
    }

    if (!isAuthBlobObject(decoded.value)) {
      safeReport(report, 'persistence_invalid_auth_payload', {
        key: USER_INFO_KEY,
        operation: 'validate',
        payloadKind: decoded.value === null ? 'null' : typeof decoded.value,
      });
      attemptClearUserInfo(driver, report);
      return null;
    }

    return decoded.value;
  }

  function writeUserInfoBlob(blob) {
    if (blob === null || blob === undefined) {
      throw new PersistenceWriteError('userInfo blob must be a plain object');
    }

    if (!isAuthBlobObject(blob)) {
      throw new PersistenceWriteError('userInfo blob must be a plain object');
    }

    const encoded = codec.encode(blob);
    if (!encoded.ok) {
      throw encoded.error;
    }

    try {
      driver.setItem(USER_INFO_KEY, encoded.value);
    } catch (cause) {
      throw new PersistenceWriteError('Failed to write userInfo blob', { cause });
    }
  }

  function clearUserInfoBlob() {
    try {
      driver.removeItem(USER_INFO_KEY);
    } catch (cause) {
      throw new PersistenceRemoveError('Failed to remove userInfo blob', { cause });
    }
  }

  function readAccessToken() {
    const blob = readUserInfoBlob();
    if (!blob) {
      return null;
    }

    const access = blob.access;
    if (typeof access === 'string' && access.length > 0) {
      return access;
    }

    return null;
  }

  function readRefreshToken() {
    const blob = readUserInfoBlob();
    if (!blob) {
      return null;
    }

    const refresh = blob.refresh;
    if (typeof refresh === 'string' && refresh.length > 0) {
      return refresh;
    }

    return null;
  }

  return {
    readUserInfoBlob,
    writeUserInfoBlob,
    clearUserInfoBlob,
    readAccessToken,
    readRefreshToken,
  };
}

export { PersistenceSerializationError };
